/**
 * @file 枚举相关 utils
 */
import { isFunction } from './lodash';
/**
 * 为枚举值映射到一套新值 (最常见的是映射到文本标签) 的场景提供一个 helper 创建器,
 * 创建并返回一个包含 4 个快捷方法的 helper:
 *
 * - get: 输入 enum 中的一个值, 返回映射后的值
 * - listOptions: 将整个映射转化为 option 数组, 比如 [{ value, label }]
 * - listKeys: 将映射的所有 key 转化为数组
 * - listValues: 将映射的所有 value 转化为数组
 *
 * 要注意如果映射后的值是 $t(...), map 需要以函数返回值的形式传入,
 * 即 `createEnumHelper(() => { xx: $t('xx'), ... })`.
 *
 * @category Enum
 * @param map - 枚举值到目标值的映射
 * @returns 包含快捷方法的 helper
 */
export const createEnumHelper = (map) => {
    const getMap = () => (isFunction(map) ? map() : map);
    const get = (key) => getMap()[key];
    const listOptions = (sourceKey, mappedKey) => Object.entries(getMap()).map(([source, mapped]) => ({
        [sourceKey]: source,
        [mappedKey]: mapped,
    }));
    const listKeys = () => Object.keys(getMap());
    const listValues = () => Object.values(getMap());
    return { get, listOptions, listKeys, listValues };
};
/**
 * 为布尔枚举值映射到一套新值 (最常见的是映射到文本标签) 的场景提供一个 helper 创建器,
 * 创建并返回一个包含 2 个快捷方法的 helper:
 *
 * - get: 输入 enum 中的一个值, 返回映射后的值
 * - listOptions: 将整个映射转化为 option 数组, 比如 [{ value, label }]
 *
 * @category Enum
 * @param boolArr - 布尔枚举值到目标值的映射
 * @returns 包含快捷方法的 helper
 * @example
 * ```javascript
 * createBoolEnumHelper([trueColor, falseColor])
 * createBoolEnumHelper(() => [$t('是'), $t('否')])
 * ```
 */
export const createBoolEnumHelper = (boolArr) => {
    const getBoolArr = () => (isFunction(boolArr) ? boolArr() : boolArr);
    const get = (key) => getBoolArr()[key ? 0 : 1];
    const listOptions = (sourceKey, mappedKey) => {
        const [truthy, falsy] = getBoolArr();
        return [
            {
                [sourceKey]: true,
                [mappedKey]: truthy,
            },
            {
                [sourceKey]: false,
                [mappedKey]: falsy,
            },
        ];
    };
    return {
        get,
        listOptions,
    };
};
//# sourceMappingURL=data:application/json;base64,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